<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.WRITE_OFFS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.WRITE_OFFS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ document.code || $t("STOCK.WRITE_OFFS.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canFinalize" class="mr-2 success" small @click="finalizeWriteOff">
            {{ $t("STOCK.WRITE_OFFS.FINALIZE") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardWriteOff">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSave" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-alert
                v-if="isReadOnlyDocument"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.WRITE_OFFS.READ_ONLY_DOCUMENT") }}
              </v-alert>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.source_warehouse_id"
                    :items="warehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WRITE_OFFS.SOURCE_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.WRITE_OFFS.SOURCE_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.source_warehouse_id || []"
                    @change="clearHeaderValidationError('source_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.reason_id"
                    :items="reasonOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WRITE_OFFS.REASON') + '*'"
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.reason_id || []"
                    @change="clearHeaderValidationError('reason_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="document.action_date"
                    type="date"
                    :label="$t('STOCK.WRITE_OFFS.DATE') + '*'"
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="document.notes"
                    :label="$t('STOCK.WRITE_OFFS.NOTES')"
                    :disabled="!canSave"
                    rows="2"
                    auto-grow
                    :error-messages="validationErrors.notes || []"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-subheader class="px-0">{{ $t("STOCK.SALES_ORDERS.LINES") }}</v-subheader>

              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <div v-else class="stock-document-lines-wrap">
                <v-simple-table dense class="stock-document-lines">
                  <thead>
                    <tr>
                      <th class="stock-document-lines__sticky stock-document-lines__sticky--index">
                        #
                      </th>
                      <th
                        class="stock-document-lines__sticky stock-document-lines__sticky--product"
                      >
                        {{ $t("STOCK.PRODUCTS.PRODUCT") }}
                      </th>
                      <th class="stock-document-lines__col-qty">
                        {{ $t("STOCK.SALES_ORDERS.QTY") }}
                      </th>
                      <th class="stock-document-lines__col-qty">
                        {{ $t("STOCK.COMMON.GROSS_WEIGHT_KG") }}
                      </th>
                      <th class="stock-document-lines__col-lot">
                        {{ $t("STOCK.WRITE_OFFS.LOT") }}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(line, index) in lines"
                      :key="`line-${index}-${line.stock_product_id}`"
                      :class="{
                        'stock-document-lines__row--lot-error':
                          lineExceedsLotAvailable(line, lotQuantityFieldKey),
                        'stock-document-lines__row--no-stock':
                          lineHasNoAvailableLots(line),
                      }"
                    >
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--index"
                      >
                        {{ index + 1 }}
                      </td>
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--product stock-document-lines__product"
                      >
                        <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                        <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineSyncedQtyFields
                          :line="line"
                          :disabled="!canEditLineFields(line)"
                          :error="lineExceedsLotAvailable(line, lotQuantityFieldKey)"
                          :kg-hint="
                            line.stock_lot_id
                              ? lineLotQuantityHint(line, lotQuantityFieldKey, lineUnitCode(line))
                              : lineUnitCode(line) || 'kg'
                          "
                          @focus="focusSelectAllInput"
                          @quantity-changed="recalculateGrossWeightFromLot(line)"
                        />
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineField
                          v-model.number="line.gross_weight_kg"
                          type="number"
                          hint="kg"
                          :disabled="!canEditLineFields(line)"
                          @focus="focusSelectAllInput"
                        />
                      </td>
                      <td class="stock-document-lines__col-lot">
                        <template v-if="usesLotPicker(line)">
                          <v-autocomplete
                            v-if="canSave || canFinalize"
                            v-model="line.stock_lot_id"
                            :items="lotOptionsForLine(line)"
                            item-text="label"
                            item-value="id"
                            dense
                            hide-details
                            clearable
                            :error="lineExceedsLotAvailable(line, lotQuantityFieldKey)"
                            :disabled="!canSave && !canFinalize"
                            :placeholder="$t('STOCK.WRITE_OFFS.LOT_PLACEHOLDER')"
                            @change="onLotSelected(line, $event)"
                          ></v-autocomplete>
                          <span v-else>{{ lineLotDisplay(line) }}</span>
                          <StockLineLotAvailability
                            :product="line.stock_product"
                            :available-kg="lineLotAvailableQuantity(line, lotQuantityFieldKey)"
                          />
                        </template>
                        <span
                          v-else-if="lineHasNoAvailableLots(line)"
                          class="caption warning--text"
                        >
                          {{ $t("STOCK.WRITE_OFFS.NO_AVAILABLE_STOCK") }}
                        </span>
                        <span v-else>{{ lineLotDisplay(line) }}</span>
                      </td>
                      <td class="stock-document-lines__actions text-nowrap">
                        <v-btn
                          v-if="canEditLineStructure && line.stock_product_id"
                          icon
                          small
                          :title="$t('STOCK.WRITE_OFFS.DUPLICATE_PRODUCT_LINE')"
                          @click="duplicateProductLine(index)"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn v-if="canEditLineStructure" icon small @click="removeLine(index)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <StockProductLineFinder
                :disabled="!canEditLineStructure"
                @add="addProductFromFinder"
              />

              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.WRITE_OFFS.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="mb-4">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockDocumentFilesPanel
                v-if="!isNew"
                document-kind="write_off"
                :document-id="writeOffId"
                :files="documentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockLineSyncedQtyFields from "@/views/partials/stock/StockLineSyncedQtyFields.vue";
import StockLineLotAvailability from "@/views/partials/stock/StockLineLotAvailability.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockStatusTimelineMixin from "@/views/partials/stock/stockStatusTimeline.mixin";
import stockLotQuantityValidationMixin from "@/views/partials/stock/stockLotQuantityValidation.mixin";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_WRITE_OFF,
  CREATE_STOCK_WRITE_OFF,
  UPDATE_STOCK_WRITE_OFF,
  FINALIZE_STOCK_WRITE_OFF,
  DISCARD_STOCK_WRITE_OFF,
  GET_STOCK_WRITE_OFF_SUGGEST_LOTS,
  GET_STOCK_WRITE_OFF_SUGGEST_DRAFT_LOTS,
  GET_STOCK_WRITE_OFF_REASON_OPTIONS,
} from "@/store/stock/stockWriteOffs.module";

const emptyLine = () => ({
  stock_product_id: null,
  quantity: 1,
  secondary_quantity: null,
  packaging_quantities: {},
  unit_id: null,
  unit_code: null,
  gross_weight_kg: null,
  gross_to_net_ratio: null,
  lot_number: "",
  stock_lot_id: null,
  notes: null,
});

export default {
  name: "StockViewWriteOff",
  components: {
    StockProductLineFinder,
    StockLineField,
    StockLineSyncedQtyFields,
    StockLineLotAvailability,
    StockDocumentFilesPanel,
    StockDocumentPageHeading,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentContextMixin,
    stockStatusTimelineMixin,
    stockLotQuantityValidationMixin,
  ],
  data() {
    return {
      lotQuantityFieldKey: "quantity",
      statusTimelineI18nPrefix: "STOCK.STATUSES.WRITE_OFF",
      lineQuantityKey: "quantity",
      tab: "main",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      lotSuggestions: [],
      lotSuggestionsReady: false,
      organizations: [],
      warehouseOptions: [],
      reasonOptions: [],
      validationErrors: {},
      documentFiles: [],
    };
  },
  computed: {
    writeOffId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    isNew() {
      return !this.$route.params.id;
    },
    canSave() {
      if (this.isReadOnlyDocument) {
        return false;
      }
      return this.isNew
        ? this.hasPermissions("stock-write-offs-create")
        : this.hasPermissions("stock-write-offs-edit");
    },
    canEditLineStructure() {
      return this.canSave;
    },
    canFinalize() {
      return (
        !this.isNew &&
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-write-offs-finalize")
      );
    },
    isPhysicalLotPickerEnabled() {
      return true;
    },
    lotSuggestionSignature() {
      const productIds = [
        ...new Set(
          (this.lines || [])
            .map((line) => line.stock_product_id)
            .filter(Boolean)
            .map(Number)
        ),
      ].sort((a, b) => a - b);
      return `${this.document.source_warehouse_id || ""}|${productIds.join(",")}`;
    },
    isReadOnlyDocument() {
      if (this.isNew) {
        return false;
      }
      const slug = this.document.action_type && this.document.action_type.slug;
      return slug !== "write_off" || !["draft", "expected"].includes(this.document.status);
    },
    canDiscard() {
      if (this.isNew) {
        return true;
      }
      return (
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-write-offs-cancel")
      );
    },
    canRegenerateDocument() {
      return this.document.status === "finalized";
    },
    statusColor() {
      const map = {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        cancelled: "error",
        reversed: "purple",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.WRITE_OFF.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    statusTimelineColorMap() {
      return {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
    },
  },
  watch: {
    "$route.params.id"(id, previousId) {
      if (!id || id === previousId) {
        return;
      }
      if (String(this.document.id) === String(id)) {
        return;
      }
      this.loadWriteOff(id);
    },
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
    },
    lotSuggestionSignature() {
      this.lotSuggestionsReady = false;
      this.queueLotSuggestionsLoad();
    },
  },
  created() {
    this.queueLotSuggestionsLoad = debounce(() => {
      this.loadLotSuggestions();
    }, 300);
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    emptyDocument() {
      return {
        organization_id: null,
        source_warehouse_id: null,
        reason_id: null,
        action_date: new Date().toISOString().slice(0, 10),
        notes: null,
        status: "draft",
        code: null,
      };
    },
    computeLineNetFromUnit() {
      return 0;
    },
    validateOrganizationAndWarehouseRequired() {
      let valid = true;
      const errors = { ...(this.validationErrors || {}) };

      if (!this.document.organization_id) {
        errors.organization_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.organization_id;
      }

      if (!this.document.source_warehouse_id) {
        errors.source_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.source_warehouse_id;
      }

      if (!this.document.reason_id) {
        errors.reason_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.reason_id;
      }

      this.validationErrors = errors;

      if (!valid) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.WRITE_OFFS.ORG_WAREHOUSE_AND_REASON_REQUIRED_SAVE"),
          color: "error",
        });
      }

      return valid;
    },
    organizationIdsForWarehouseQuery() {
      const docOrgId = this.document.organization_id
        ? Number(this.document.organization_id)
        : null;

      if (docOrgId) {
        return [docOrgId];
      }

      const contextOrgIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      if (contextOrgIds.length > 0) {
        return contextOrgIds;
      }

      return [];
    },
    async loadOrganizations() {
      const contextIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      const params = {};
      if (contextIds.length > 0) {
        params.organization_ids = contextIds;
      }

      const orgs = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, params);
      let list = orgs.data || orgs;

      if (contextIds.length > 0) {
        list = list.filter((org) => contextIds.includes(Number(org.id)));
      }

      this.organizations = list;
    },
    async loadWarehouses() {
      const params = {
        organization_ids: this.organizationIdsForWarehouseQuery(),
      };
      const contextWarehouseIds = (this.$store.getters.stockContextWarehouseIds || []).map(Number);
      const selectedWarehouseIds = [this.document.source_warehouse_id]
        .filter(Boolean)
        .map(Number);
      const warehouseIdsParam = [
        ...new Set([...contextWarehouseIds, ...selectedWarehouseIds]),
      ];
      if (warehouseIdsParam.length > 0) {
        params.warehouse_ids = warehouseIdsParam;
      }

      const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, params);
      const warehouses = data.data || data;
      let options = this.mapWarehouseOptions(warehouses, ["physical"]);

      if (contextWarehouseIds.length > 0) {
        options = options.filter(
          (warehouse) =>
            contextWarehouseIds.includes(Number(warehouse.id)) ||
            selectedWarehouseIds.includes(Number(warehouse.id))
        );
      }

      this.warehouseOptions = options;

      selectedWarehouseIds.forEach((warehouseId) => {
        const selected = warehouses.find(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        );
        if (selected && selected.type === "physical") {
          this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, {
            ...selected,
            label: selected.label || this.warehouseOptionLabel(selected),
          });
        }
      });

      if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
        this.reconcileHeaderOptionsFromSeed();
      }
    },
    async loadReasons() {
      const data = await this.$store.dispatch(GET_STOCK_WRITE_OFF_REASON_OPTIONS);
      this.reasonOptions = (Array.isArray(data) ? data : []).map((reason) => ({
        id: Number(reason.id),
        label: reason.label || reason.name,
        slug: reason.slug,
        scope: reason.scope,
      }));
    },
    resetDocumentSelectionsOutsideContext() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      const orgId = this.document.organization_id;
      if (
        orgId &&
        !this.organizations.some((org) => Number(org.id) === Number(orgId))
      ) {
        if (!(this.isNew && this.headerOptionsSeed)) {
          this.document.organization_id = null;
          this.document.source_warehouse_id = null;
        }
        return;
      }

      const warehouseId = this.document.source_warehouse_id;
      if (
        warehouseId &&
        !this.warehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.warehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(warehouseId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.source_warehouse_id = null;
        }
      }
    },
    applyStockContextDefaultsOnNewDocument() {
      if (!this.isNew) {
        return;
      }

      if (!this.document.organization_id && this.organizations.length === 1) {
        this.document.organization_id = this.organizations[0].id;
      }

      if (!this.document.source_warehouse_id && this.warehouseOptions.length === 1) {
        this.document.source_warehouse_id = this.warehouseOptions[0].id;
      }

      this.applyRouteQueryDefaultsOnNewDocument();
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await Promise.all([this.loadWarehouses(), this.loadReasons()]);
        if (!this.isNew) {
          await this.loadWriteOff(this.$route.params.id);
        } else {
          await this.withSuppressedStockHeaderReload(async () => {
            this.applyStockContextDefaultsOnNewDocument();
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadWriteOff(id) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(GET_STOCK_WRITE_OFF, id);
        await this.applyDocument(data);
        await this.loadLotSuggestions();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadLotSuggestions() {
      this.lotSuggestionsReady = false;

      try {
        if (!this.isNew && this.writeOffId) {
          const data = await this.$store.dispatch(GET_STOCK_WRITE_OFF_SUGGEST_LOTS, {
            writeOffId: this.writeOffId,
          });
          this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
          return;
        }

        const warehouseId = this.document.source_warehouse_id;
        const productIds = [
          ...new Set(
            (this.lines || [])
              .map((line) => line.stock_product_id)
              .filter(Boolean)
              .map(Number)
          ),
        ];

        if (!warehouseId || !productIds.length) {
          this.lotSuggestions = [];
          return;
        }

        const data = await this.$store.dispatch(GET_STOCK_WRITE_OFF_SUGGEST_DRAFT_LOTS, {
          params: {
            source_warehouse_id: warehouseId,
            stock_product_ids: productIds,
            as_of_date: this.document.action_date,
          },
        });
        this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
      } catch (e) {
        this.lotSuggestions = [];
      } finally {
        this.lotSuggestionsReady = true;
      }
    },
    lineHasNoAvailableLots(line) {
      if (!line?.stock_product_id || line.stock_lot_id) {
        return false;
      }
      if (!this.document.source_warehouse_id || !this.lotSuggestionsReady) {
        return false;
      }
      return this.lotOptionsForLine(line).length === 0;
    },
    canEditLineFields(line) {
      return this.canSave && !this.lineHasNoAvailableLots(line);
    },
    lotOptionsForLine(line) {
      if (!line.stock_product_id) {
        return [];
      }

      return this.mapLotPickerOptions(line, this.lotSuggestions);
    },
    recalculateGrossWeightFromLot(line) {
      const option = this.selectedLotOptionForLine(line);
      const ratio = option?.default_gross_to_net_ratio;
      if (!ratio || Number(ratio) <= 0) {
        return;
      }

      line.gross_weight_kg =
        Math.round(Number(line.quantity || 0) * Number(ratio) * 10000) / 10000;
    },
    onLotSelected(line, lotId) {
      if (!lotId) {
        line.lot_number = "";
        line.gross_weight_kg = null;
        return;
      }
      const option = this.lotOptionsForLine(line).find(
        (item) => Number(item.id) === Number(lotId)
      );
      if (option) {
        line.lot_number = option.lot_number || option.label || "";
        this.recalculateGrossWeightFromLot(line);
      }
    },
    mapWriteOffDocument(data) {
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        source_warehouse_id: data.source_warehouse_id ?? data.source_warehouse?.id ?? null,
        reason_id: data.reason_id ?? data.reason?.id ?? null,
      };
    },
    syncWriteOffHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.source_warehouse) {
        const wh = {
          ...data.source_warehouse,
          label: this.warehouseOptionLabel(data.source_warehouse),
        };
        this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, wh);
      } else {
        this.syncWarehouseOptionFromDraft(data, "source_warehouse_id", "source_warehouse");
      }
      if (data.reason) {
        this.reasonOptions = this.mergeOptionIntoList(this.reasonOptions, {
          id: Number(data.reason.id),
          label: data.reason.label || data.reason.name,
          slug: data.reason.slug,
          scope: data.reason.scope,
        });
      }
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          ...this.mapWriteOffDocument(data),
        };
        await this.loadWarehouses();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncWriteOffHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncWriteOffHeaderOptions(data);
      });
      this.lines = (data.items || []).map((item) => {
        const line = {
          id: item.id,
          stock_product_id: item.stock_product_id,
          stock_product: item.stock_product || null,
          quantity: Number(item.quantity || 0),
          secondary_quantity: null,
          packaging_quantities: {},
          ...this.mapLineUnitFields(item),
          lot_number: item.lot_number || item.stock_lot?.lot_number || "",
          stock_lot_id: item.stock_lot_id || item.stock_lot?.id || null,
          stock_lot: item.stock_lot || null,
          gross_weight_kg: item.gross_weight_kg,
          gross_to_net_ratio: item.gross_to_net_ratio,
          weight_basis: item.weight_basis,
          notes: item.notes,
        };

        return this.initializeLinePackagingQuantities(line);
      });
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
    },
    payload() {
      return {
        organization_id: this.document.organization_id,
        source_warehouse_id: this.document.source_warehouse_id,
        action_date: this.document.action_date,
        reason_id: this.document.reason_id,
        notes: this.document.notes,
        items: this.lines
          .filter((l) => l.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            quantity: line.quantity,
            unit_id: line.unit_id,
            gross_weight_kg: line.gross_weight_kg,
            stock_lot_id: line.stock_lot_id,
          })),
      };
    },
    async save() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      if (!this.validateLotQuantitiesOrNotify(this.lotQuantityFieldKey)) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_WRITE_OFF, params);
          this.$router.replace({
            name: "stock-write-offs-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_WRITE_OFF, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        await this.loadLotSuggestions();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async finalizeWriteOff() {
      await this.save();
      if (this.isNew || this.isReadOnlyDocument) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          FINALIZE_STOCK_WRITE_OFF,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.WRITE_OFFS.FINALIZED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async discardWriteOff() {
      if (this.isNew) {
        this.$router.back();
        return;
      }

      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }

      this.isProcessing = true;
      try {
        await this.$store.dispatch(DISCARD_STOCK_WRITE_OFF, this.$route.params.id);
        this.$router.push({ name: "stock-movements" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    duplicateProductLine(index) {
      const line = this.lines[index];
      if (!line?.stock_product_id || !line.stock_product) {
        return;
      }
      this.insertProductLineAfter(line.stock_product, emptyLine, index);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 260px;
  width: 260px;
  max-width: 280px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky--index,
.stock-document-lines thead .stock-document-lines__sticky--product {
  background: #f5f5f5;
  z-index: 3;
}
.stock-document-lines__col-qty {
  min-width: 128px;
  width: 130px;
  vertical-align: top;
}
.stock-document-lines__col-lot {
  min-width: 160px;
  width: 180px;
  vertical-align: top;
}
.stock-document-lines__row--lot-error {
  background: rgba(244, 67, 54, 0.06);
}
.stock-document-lines__row--no-stock {
  background: rgba(255, 152, 0, 0.08);
}
</style>
